import { ApolloClient, InMemoryCache } from "@apollo/client";
import { BatchHttpLink } from "@apollo/client/link/batch-http";
import { onError } from "@apollo/client/link/error";
import introspectionQueryResultData from "fragmentTypes.json";

// Apollo 3 dropped IntrospectionFragmentMatcher in favour of a plain map of
// interface/union name to the concrete types that satisfy it. Derived from the
// same generated fragmentTypes.json so there is nothing extra to maintain.
const possibleTypes = introspectionQueryResultData.__schema.types.reduce(
    (acc, type) => {
        acc[type.name] = (type.possibleTypes || []).map((possible) => possible.name);
        return acc;
    },
    {}
);

const createClient = (params) => {
    const isClient = typeof window !== "undefined";
    const cache = new InMemoryCache({ possibleTypes });

    if (isClient && window.__APOLLO_STATE__) {
        cache.restore(window.__APOLLO_STATE__);
    }

    // Global fetch, which every supported browser has and Node has had since 18.
    // This file is bundled for the browser, so importing node-fetch here forced
    // webpack to resolve http/https/stream — harmless under webpack 4, which
    // polyfilled them automatically, but a build failure under webpack 5.
    const fetchClt = (...args) => fetch(...args);

    // Craft's live preview passes ?token=… and expects it back as a header. This
    // used to be handed to the ApolloClient constructor, which accepts no
    // `headers` option in either v2 or v3 and silently ignored it — so the token
    // was never actually sent. It belongs on the link.
    const craftToken = isClient
        ? (window.location.search.match(/\btoken=([^&]+)/) || [])[1] || ""
        : "";

    // Batched transport. A page render fires roughly seven independent queries —
    // the page itself plus globals, nav, footer, page options, alert bar and
    // cookie modal — and each used to be its own HTTP round trip to /data and on
    // to Craft. Craft's GraphQL endpoint accepts an array of operations natively,
    // so collecting them into one request needs no component changes.
    const httpLink = new BatchHttpLink({
        uri: isClient ?  `/data?pathname=${window.location.pathname}` : `${params && params.baseUrl ? params.baseUrl : "" }/data`,
        credentials: "same-origin",
        fetch: fetchClt,
        headers: { "X-Craft-Token": craftToken },
        // prerenderStatic renders in passes, so a batch is whatever one pass
        // issues. The window only has to outlast a synchronous render pass.
        batchInterval: 20,
        batchMax: 12,
    });

    const errorLink = onError(({ operation, networkError }) => {
        const context = operation.getContext();
        let errText = '';

        if (context && context.response) {
            let suberrContext = '|' + 'ERROR: |' + 'URL: ' + context.response.url + '|' + 'Status: ' + context.response.status + ' - ' + context.response.statusText;
            console.log(suberrContext.replace('|', "\n"));
            errText += suberrContext;
        }

        if (networkError) {
            let suberrNetwork = '|' + 'Body text: ' + networkError.bodyText + '|' + `[Network error]: ${networkError}`;
            if (!networkError.bodyText) {
              suberrNetwork += '|' + 'Seems like it\'s client-side error...';
            }

            console.log(suberrNetwork.replace('|', "\n"));
            errText += suberrNetwork;
        }

        console.log("\n");

        // Browser only. Server-side fetch — node-fetch before, the global one now —
        // rejects a relative URL, and this runs inside an error handler where
        // nothing caught it, so the SSR process exited: any failed GraphQL call
        // became several seconds of downtime while the supervisor restarted it.
        // On the server the error is already on stdout from the console.log above.
        if (isClient) {
            const params = new URLSearchParams();
            params.append('data', errText);

            try {
                const reported = fetchClt('/report-issue', { method: 'post', body: params });
                if (reported && typeof reported.catch === 'function') {
                    reported.catch(() => {});
                }
            } catch (reportErr) {
                console.log('Could not report issue:', reportErr.message);
            }
        }
    });

    const link = errorLink.split((operation) => operation.getContext().version !== '', httpLink);

    return new ApolloClient({
        link,
        cache,
        ssrForceFetchDelay: 100
    })
}

export default createClient
